import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import {
  Avatar,
  Badge,
  Box,
  Button,
  Card,
  CardContent,
  CardProps,
  Checkbox,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  FormGroup,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  styled,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import PersonIcon from '@mui/icons-material/Person';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { useNavigate } from 'react-router';
import { useLocation } from 'react-router-dom';
import ReactPhoneInput from 'react-phone-input-material-ui';
import { ROUTE } from 'route/RouteEnums';
import { useAppDispatch } from 'store/hooks';
import { Page } from 'components/Page';
import { yupResolver } from '@hookform/resolvers/yup';
import { useForm } from 'react-hook-form';

import { deleteUser, getUser, saveUser, updateUser } from 'store/slices/userManagementSlice';
import { userManagementSchema } from 'utils/schema';
import { toast } from 'react-toastify';
import { AuthContext } from 'utils/AuthContext';
import { Locations, Positions, Roles } from 'utils/dropDownList';
import { getGlobalProfilesByUser, getProfiles } from 'store/slices/patientInfoSlice';
import { dir } from 'console';
import { getDefaultLandingPage } from 'utils/navigationUtils';
import { getAllRolesAPI } from 'services/roleManagementServices';
import { StyledDialog } from 'theme/index';

const StyleCard = styled(Card)<CardProps>(({ theme }) => ({
  boxShadow: '0px 4px 16px 0px rgba(0, 0, 0, 0.12)',
  border: '1px solid #EFF4FF',
  borderRadius: 12,
  background: '#FFF',
  margin: '12px 16px',
  position: 'relative',
  zIndex: 99,
  padding: 20,
  '& .MuiTypography-root': {
    position: 'relative',
  },
  '& .MuiCardContent-root:last-child': {
    padding: 0,
  },
  '.MuiTypography-h2': {
    fontSize: 20,
  },
  '& .Mui-selected': {
    background: '#9F84FF',
    color: '#FFF!important',
    borderRadius: '23px',
    width: '100%',
  },
  '& .MuiTab-root': {
    margin: '0 60px!important',
  },
}));

function CreateUser() {
  const {
    setValue,
    watch,
    register,
    handleSubmit,
    formState: { errors, isValid, isDirty },
  } = useForm({
    resolver: yupResolver(userManagementSchema),
    mode: 'all',
  });

  const [showConfirmationModal, setShowConfirmationModal] = useState<boolean>(false);
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [status, setStatus] = useState<string>();
  const { state } = useLocation();
  const { user } = useContext(AuthContext);
  const [globalProfileList, setGlobalProfileList] = useState<any[]>([]);
  const [roles, setRoles] = useState<any[]>([]);
  const [profileImagePreview, setProfileImagePreview] = useState<string | null>(null);
  const [selectedGlobalProfileIds, setSelectedGlobalProfileIds] = useState<number[]>([]);
  const roleType: string = watch('roleType');
  const isProviderRole = roleType?.toLowerCase() === 'provider';
  const isAdminRole = roleType?.toLowerCase() === 'admin';
  const isProvider = isProviderRole || isAdminRole;

  useEffect(() => {
    if (!user?.permissions.includes('USER_MANAGEMENT')) {
      navigate(getDefaultLandingPage());
    }
    if (state?.userId) {
      dispatch(getUser(state?.userId))
        .unwrap()
        .then((res: any) => {
          if (res?.status === 200) {
            const data = res?.data;
            setValue('firstName', data?.firstName);
            setValue('lastName', data?.lastName);
            setValue('email', data?.email);
            setValue('mobileNumber', data?.mobileNumber);
            setValue('position', data?.position);
            setValue('roleType', data?.roleType);
            setValue('providerId', data?.providerId);
            setValue('zoomLink', data?.zoomLink);
            setValue('credentials', data?.credentials);
            setValue('description', data?.description);
            // backend returns profile_picture as a bare base64 string (bytea -> byte[] -> JSON), not a data URI
            setValue('profilePicture', data?.profilePicture);
            setProfileImagePreview(
              data?.profilePicture ? `data:image/png;base64,${data.profilePicture}` : null
            );
            setStatus(data?.status);
            const profileIds = parseProfileIds(data.globalProfileSettings);
            setSelectedGlobalProfileIds(profileIds);
          }
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state?.userId]);

  useEffect(() => {
    getAllRolesAPI()
      .then((res: any) => {
        if (res?.status === 200) {
          setRoles(res.data);
        }
      })
      .catch((err) => {
        console.error('Error fetching roles', err);
      });
  }, []);

  const handleProfileImageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    if (!['image/png', 'image/jpeg'].includes(file.type)) {
      toast('File type must be one of jpg, jpeg or png', {
        type: 'error',
        icon: true,
      });
      return;
    }
    if (file.size > 15000000) {
      toast('Please upload file with size 15 MB or below', {
        type: 'error',
        icon: true,
      });
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = reader.result as string;
      setProfileImagePreview(dataUrl);
      // strip the "data:image/...;base64," prefix - backend field is byte[] (bytea), not a data URI
      setValue('profilePicture', dataUrl.split(',')[1], { shouldDirty: true });
    };
    reader.readAsDataURL(file);
  };

  const handleCheckboxChange = (id: number) => {
    setSelectedGlobalProfileIds((prev) => {
      return prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id];
    });
  };

  const groupedProfiles = useMemo(() => {
    return globalProfileList.reduce((acc: Record<string, any[]>, profile: any) => {
      if (!profile?.name) return acc;
      const firstWord = profile.name.split(' ')[0]; // ✅ complete first word
      if (!acc[firstWord]) {
        acc[firstWord] = [];
      }
      acc[firstWord].push(profile);
      return acc;
    }, {});
  }, [globalProfileList]);

  const parseProfileIds = (ids?: string): number[] => {
    if (!ids) return [];
    return ids
      .split(',')
      .map((id) => Number(id.trim()))
      .filter(Boolean);
  };

  const onSubmitForm = (formData: any) => {
    if (state?.userId) {
      const data = {
        ...formData,
        userId: state?.userId,
        globalProfileIds: isProvider ? selectedGlobalProfileIds.join(',') : null,
        providerId: isProvider ? formData.providerId : null,
        zoomLink: isProvider ? formData.zoomLink : null,
      };
      dispatch(updateUser(data))
        .unwrap()
        .then((res: any) => {
          if (res?.status === 200) {
            toast('User details updated successfully', {
              type: 'success',
              icon: true,
            });
            navigate(ROUTE.ADMIN_PRACTICE_MANAGE_USERS_LIST);
          }
        });
    } else {
      const data = {
        ...formData,
        globalProfileIds: isProvider ? selectedGlobalProfileIds.join(',') : null,
        providerId: isProvider ? formData.providerId : null,
        zoomLink: isProvider ? formData.zoomLink : null,
      };
      dispatch(saveUser(data))
        .unwrap()
        .then((res: any) => {
          if (res?.status === 200) {
            toast('New user account created successfully', {
              type: 'success',
              icon: true,
            });
            navigate(ROUTE.ADMIN_PRACTICE_MANAGE_USERS_LIST);
          }
        });
    }
  };

  const handleDeactivate = () => {
    dispatch(deleteUser(state?.userId))
      .unwrap()
      .then((res: any) => {
        if (res?.status === 200) {
          toast('User account deactivated successfully', {
            type: 'success',
            icon: true,
          });
          navigate(ROUTE.ADMIN_PRACTICE_MANAGE_USERS_LIST);
        }
      });
  };

  const getProfile = useCallback(() => {
    dispatch(getProfiles({}))
      .unwrap()
      .then((response) => {
        if (response?.status === 200) {
          setGlobalProfileList(response.data);
        }
      });
  }, [dispatch]);

  useEffect(() => {
    if (!isProvider) {
      setGlobalProfileList([]);
      return;
    }
    getProfile();
  }, [isProvider, getProfile]);

  return (
    <Page backDir={ROUTE.ADMIN_PRACTICE_MANAGE_USERS_LIST} buttonText='Back'>
      <Container component='main' maxWidth='md'>
        <Box component='form' onSubmit={handleSubmit((formData) => onSubmitForm(formData))}>
          <Grid container spacing={2}>
            <Grid item xs={10} display={'flex'} alignItems={'center'} mt={3} mb={3}>
              <Typography variant='h5' gutterBottom>
                {state?.userId ? 'Update User Details' : 'Create New User'}
              </Typography>
            </Grid>
            <Grid
              item
              xs={2}
              display={'flex'}
              alignItems={'center'}
              justifyContent={'flex-end'}
              mt={3}
              mb={3}
            >
              <Badge
                overlap='circular'
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                badgeContent={
                  <IconButton
                    component='label'
                    size='small'
                    sx={{
                      bgcolor: '#3467FF',
                      color: '#fff',
                      width: 28,
                      height: 28,
                      '&:hover': { bgcolor: '#3467FF' },
                    }}
                  >
                    <PhotoCameraIcon sx={{ fontSize: 16 }} />
                    <input
                      hidden
                      type='file'
                      accept='image/png, image/jpeg'
                      onChange={handleProfileImageChange}
                    />
                  </IconButton>
                }
              >
                <Avatar src={profileImagePreview ?? undefined} sx={{ width: 115, height: 115 }}>
                  <PersonIcon />
                </Avatar>
              </Badge>
            </Grid>
          </Grid>
          <StyleCard>
            <CardContent>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <InputLabel htmlFor='firstName'>First Name*</InputLabel>
                  <TextField
                    id='firstName'
                    variant='outlined'
                    fullWidth
                    {...register('firstName')}
                    error={!!errors.firstName}
                    helperText={errors?.firstName?.message}
                    inputProps={{
                      maxLength: 255,
                    }}
                    value={watch('firstName') ? watch('firstName') : ''}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <InputLabel htmlFor='lastName'>Last Name*</InputLabel>
                  <TextField
                    id='lastName'
                    variant='outlined'
                    fullWidth
                    {...register('lastName')}
                    error={!!errors.lastName}
                    helperText={errors?.lastName?.message}
                    inputProps={{
                      maxLength: 255,
                    }}
                    value={watch('lastName') ? watch('lastName') : ''}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <InputLabel htmlFor='email'>Email*</InputLabel>
                  <TextField
                    id='email'
                    variant='outlined'
                    fullWidth
                    {...register('email')}
                    error={!!errors.email}
                    helperText={errors?.email?.message}
                    inputProps={{
                      maxLength: 255,
                    }}
                    value={watch('email') ? watch('email') : ''}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <InputLabel htmlFor='mobileNumber'>Phone Number</InputLabel>
                  <ReactPhoneInput
                    component={TextField}
                    label=''
                    onlyCountries={['us']}
                    country={'us'}
                    masks={{ us: '(...) ...-....' }}
                    autoFormat={true}
                    disableDropdown={true}
                    defaultErrorMessage={'Please enter a Phone Number'}
                    placeholder='+1 (555) 000-0000'
                    {...register('mobileNumber')}
                    inputProps={{
                      helperText: errors.mobileNumber ? errors.mobileNumber?.message : '',
                      error: !!errors.mobileNumber,
                    }}
                    countryCodeEditable={false}
                    onChange={(e) => {
                      setValue('mobileNumber', e, { shouldValidate: true });
                    }}
                    value={watch('mobileNumber') ? watch('mobileNumber') : null}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <InputLabel htmlFor='position'>Position</InputLabel>
                  <TextField
                    id='position'
                    variant='outlined'
                    fullWidth
                    select
                    {...register('position')}
                    error={!!errors.position}
                    helperText={errors?.position?.message}
                    value={watch('position') ? watch('position') : ''}
                  >
                    <MenuItem key={0} value='position' disabled>
                      Choose Position
                    </MenuItem>
                    {Positions.map((l) => (
                      <MenuItem key={l.id} value={l.position}>
                        {l.position}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={12} md={6}>
                  <InputLabel htmlFor='roleType'>User Role</InputLabel>
                  <TextField
                    id='roleType'
                    variant='outlined'
                    fullWidth
                    select
                    {...register('roleType')}
                    error={!!errors.roleType}
                    helperText={errors?.roleType?.message}
                    value={watch('roleType') ? watch('roleType') : ''}
                  >
                    <MenuItem key={0} value='roleType' disabled>
                      Choose Role
                    </MenuItem>
                    {roles.map((role) => (
                      <MenuItem key={role.id} value={role.name}>
                        {role.name}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
                {isProvider && (
                  <>
                    <Grid item xs={12} md={6}>
                      <Box display='flex' alignItems='center'>
                        <InputLabel htmlFor='credentials'>Credentials</InputLabel>
                        <Tooltip title='Example: MD, PMHNP etc.' arrow>
                          <InfoOutlinedIcon
                            sx={{ fontSize: 16, ml: 0.5, mb: '10px', color: 'text.secondary' }}
                          />
                        </Tooltip>
                      </Box>
                      <TextField
                        id='credentials'
                        variant='outlined'
                        fullWidth
                        {...register('credentials')}
                        inputProps={{
                          maxLength: 255,
                        }}
                        value={watch('credentials') ? watch('credentials') : ''}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <InputLabel htmlFor='description'>Professional Description</InputLabel>
                      <TextField
                        id='description'
                        variant='outlined'
                        fullWidth
                        {...register('description')}
                        inputProps={{
                          maxLength: 1000,
                        }}
                        value={watch('description') ? watch('description') : ''}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <InputLabel htmlFor='providerId'>Provider ID</InputLabel>
                      <TextField
                        id='providerId'
                        variant='outlined'
                        fullWidth
                        {...register('providerId')}
                        inputProps={{
                          maxLength: 255,
                        }}
                        value={watch('providerId') ? watch('providerId') : ''}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      {/**
                       * MM-780: mark the Zoom link as required once a Provider ID is entered,
                       * matching the userManagementSchema rule.
                       */}
                      <InputLabel htmlFor='zoomLink'>
                        Zoom Personal Room Link{watch('providerId')?.trim() ? '*' : ''}
                      </InputLabel>
                      <TextField
                        id='zoomLink'
                        variant='outlined'
                        fullWidth
                        {...register('zoomLink')}
                        error={!!errors.zoomLink}
                        helperText={errors?.zoomLink?.message}
                        inputProps={{
                          maxLength: 255,
                        }}
                        value={watch('zoomLink') ? watch('zoomLink') : ''}
                      />
                    </Grid>
                  </>
                )}
              </Grid>
              {isProvider && (
                <Grid container mt={1} spacing={2}>
                  <Grid item xs={12}>
                    <Box
                      component='fieldset'
                      sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, px: 1 }}
                    >
                      <Box component='legend' sx={{ px: 1, fontWeight: 600, fontSize: 14 }}>
                        Select Appointment Templates
                      </Box>

                      {Object.keys(groupedProfiles)
                        .sort()
                        .map((group) => (
                          <Box key={group} mb={3}>
                            <Typography variant='subtitle2' sx={{ mb: 1, color: 'text.secondary' }}>
                              {group}
                            </Typography>

                            <FormGroup
                              sx={{
                                display: 'grid',
                                gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                                columnGap: 1,
                              }}
                            >
                              {groupedProfiles[group].map((profile) => (
                                <FormControlLabel
                                  key={profile.id}
                                  sx={{ m: 0 }}
                                  control={
                                    <Checkbox
                                      checked={selectedGlobalProfileIds.includes(profile.id)}
                                      onChange={() => handleCheckboxChange(profile.id)}
                                    />
                                  }
                                  label={`${profile.name} (${profile.duration} mins)`}
                                />
                              ))}
                            </FormGroup>
                          </Box>
                        ))}
                    </Box>
                  </Grid>
                </Grid>
              )}

              <Grid container display='flex' justifyContent='space-between' mt='20px'>
                <Grid item display='flex'>
                  {status?.toLowerCase() === 'deactivated' ? (
                    <Typography color='#d32f2f'> This user has been deactivated.</Typography>
                  ) : null}
                </Grid>
                <Grid item display='flex' justifySelf='flex-end'>
                  <Button
                    variant='text'
                    sx={{ color: '#d32f2f' }}
                    disabled={
                      state?.userId === undefined ||
                      state?.userId === null ||
                      status?.toLowerCase() === 'deactivated'
                    }
                    onClick={() => {
                      setShowConfirmationModal(true);
                    }}
                  >
                    {' '}
                    Deactivate User
                  </Button>
                </Grid>
              </Grid>
            </CardContent>
          </StyleCard>
          <Grid display={'flex'} justifyContent={'center'} mt={'20px'}>
            <Button
              variant='outlined'
              sx={{ mr: '16px' }}
              onClick={() => {
                navigate(ROUTE.ADMIN_PRACTICE_MANAGE_USERS_LIST);
              }}
            >
              Cancel
            </Button>

            <Button type='submit' variant='contained'>
              {state?.userId ? 'Update' : 'Create'}
            </Button>
          </Grid>
        </Box>
      </Container>

      <StyledDialog
        aria-labelledby='success-dialog'
        open={showConfirmationModal}
        sx={{
          '&.MuiDialog-paper': {
            p: '24px',
          },
        }}
      >
        <DialogTitle id='simple-dialog-title' sx={{ padding: '30px 30px 0 ' }}>
          <Typography variant='h2'>Deactivate Account</Typography>
        </DialogTitle>
        <DialogContent>
          <Grid
            container
            spacing={0}
            direction='column'
            alignItems='center'
            justifyContent='center'
            mt={'36px'}
          >
            <Typography sx={{ fontSize: '18px', textAlign: 'center' }}>
              Deactivating this account will remove the VMA’s access to the MindWeal platform.{' '}
            </Typography>
            <Typography sx={{ fontSize: '18px', mt: '20px' }}>
              Are you sure you want to proceed?
            </Typography>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ display: 'flex', justifyContent: 'center' }}>
          <Button
            variant='outlined'
            onClick={() => setShowConfirmationModal(false)}
            sx={{ mr: '20px' }}
          >
            {' '}
            Cancel
          </Button>
          <Button
            onClick={() => handleDeactivate()}
            disabled={state?.userId === undefined || state?.userId === null}
            sx={{
              minWidth: { xs: '140px', sm: '200px' },
              background: '#F04438',
              color: '#fff',
              '&:hover': {
                background: '#F04438',
              },
            }}
          >
            {' '}
            Deactivate{' '}
          </Button>
        </DialogActions>
      </StyledDialog>
    </Page>
  );
}

export default CreateUser;
